import { z } from "zod";

import { FileType } from "./file";

export const MimeTypeValues = [
  "audio/aac",
  "application/x-abiword",
  "application/x-freearc",
  "video/x-msvideo",
  "application/vnd.amazon.ebook",
  "application/octet-stream",
  "image/bmp",
  "application/x-bzip",
  "application/x-bzip2",
  "application/x-cdf",
  "application/x-csh",
  "audio/amr",
  "text/css",
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-fontobject",
  "application/epub+zip",
  "application/gzip",
  "image/gif",
  "text/html",
  "image/vnd.microsoft.icon",
  "text/calendar",
  "application/java-archive",
  "image/jpeg",
  "text/javascript",
  "application/json",
  "application/ld+json",
  "audio/midi",
  "audio/x-midi",
  "audio/mpeg",
  "audio/mp4",
  "audio/x-m4a",
  "video/mp4",
  "application/mp4",
  "video/mpeg",
  "application/vnd.apple.installer+xml",
  "application/vnd.oasis.opendocument.presentation",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.text",
  "audio/ogg",
  "video/ogg",
  "application/ogg",
  "audio/opus",
  "font/otf",
  "image/png",
  "application/x-cfb",
  "application/pdf",
  "application/x-httpd-php",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.rar",
  "application/rtf",
  "application/x-sh",
  "image/svg+xml",
  "application/x-shockwave-flash",
  "application/x-tar",
  "image/tiff",
  "video/mp2t",
  "font/ttf",
  "text/plain",
  "text/vcard",
  "text/x-vcard",
  "application/vnd.visio",
  "audio/wav",
  "audio/webm",
  "video/webm",
  "image/webp",
  "font/woff",
  "font/woff2",
  "application/xhtml+xml",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/xml",
  "application/vnd.mozilla.xul+xml",
  "application/zip",
  "video/3gpp",
  "video/3gpp2",
  "video/quicktime",
  "application/x-7z-compressed",
  "image/jp2",
  "image/heic",
  "image/heif",
  "image/avif",
  "image/x-adobe-dng",
  "application/vnd.xactware.esx",
  "message/rfc822",
  "multipart/related",
] as const;

const MimeTypeEnum = z.enum(MimeTypeValues);

export const MimeTypeSchema = z.preprocess(
  (value) => (typeof value === "string" ? normalizeContentType(value) : value),
  MimeTypeEnum,
);
export type MimeType = z.infer<typeof MimeTypeEnum>;
// Read off the bare enum: z.preprocess returns a ZodPipe, which has no `.enum`.
export const MimeType = MimeTypeEnum.enum;

export function parseMimeType(value: unknown): MimeType | undefined {
  const parsed = MimeTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

const mimeTypeMap: Record<string, MimeType> = {
  aac: MimeType["audio/aac"],
  abw: MimeType["application/x-abiword"],
  amr: MimeType["audio/amr"],
  arc: MimeType["application/x-freearc"],
  avi: MimeType["video/x-msvideo"],
  azw: MimeType["application/vnd.amazon.ebook"],
  bin: MimeType["application/octet-stream"],
  bmp: MimeType["image/bmp"],
  bz: MimeType["application/x-bzip"],
  bz2: MimeType["application/x-bzip2"],
  cda: MimeType["application/x-cdf"],
  csh: MimeType["application/x-csh"],
  css: MimeType["text/css"],
  csv: MimeType["text/csv"],
  doc: MimeType["application/msword"],
  docx: MimeType["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  eot: MimeType["application/vnd.ms-fontobject"],
  epub: MimeType["application/epub+zip"],
  gz: MimeType["application/gzip"],
  gif: MimeType["image/gif"],
  htm: MimeType["text/html"],
  html: MimeType["text/html"],
  ico: MimeType["image/vnd.microsoft.icon"],
  ics: MimeType["text/calendar"],
  jar: MimeType["application/java-archive"],
  jp2: MimeType["image/jp2"],
  jpeg: MimeType["image/jpeg"],
  jpg: MimeType["image/jpeg"],
  js: MimeType["text/javascript"],
  json: MimeType["application/json"],
  jsonld: MimeType["application/ld+json"],
  mid: MimeType["audio/midi"],
  midi: MimeType["audio/midi"],
  mjs: MimeType["text/javascript"],
  mp3: MimeType["audio/mpeg"],
  m4a: MimeType["audio/mp4"],
  mp4: MimeType["video/mp4"],
  m4v: MimeType["video/mp4"],
  mpeg: MimeType["video/mpeg"],
  mpg: MimeType["video/mpeg"],
  mpkg: MimeType["application/vnd.apple.installer+xml"],
  odp: MimeType["application/vnd.oasis.opendocument.presentation"],
  ods: MimeType["application/vnd.oasis.opendocument.spreadsheet"],
  odt: MimeType["application/vnd.oasis.opendocument.text"],
  oga: MimeType["audio/ogg"],
  ogv: MimeType["video/ogg"],
  ogx: MimeType["application/ogg"],
  opus: MimeType["audio/opus"],
  otf: MimeType["font/otf"],
  png: MimeType["image/png"],
  pdf: MimeType["application/pdf"],
  php: MimeType["application/x-httpd-php"],
  ppt: MimeType["application/vnd.ms-powerpoint"],
  pptx: MimeType["application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  rar: MimeType["application/vnd.rar"],
  rtf: MimeType["application/rtf"],
  sh: MimeType["application/x-sh"],
  svg: MimeType["image/svg+xml"],
  swf: MimeType["application/x-shockwave-flash"],
  tar: MimeType["application/x-tar"],
  tif: MimeType["image/tiff"],
  tiff: MimeType["image/tiff"],
  ts: MimeType["video/mp2t"],
  ttf: MimeType["font/ttf"],
  txt: MimeType["text/plain"],
  vcf: MimeType["text/vcard"],
  vsd: MimeType["application/vnd.visio"],
  wav: MimeType["audio/wav"],
  weba: MimeType["audio/webm"],
  webm: MimeType["video/webm"],
  webp: MimeType["image/webp"],
  woff: MimeType["font/woff"],
  woff2: MimeType["font/woff2"],
  xhtml: MimeType["application/xhtml+xml"],
  xls: MimeType["application/vnd.ms-excel"],
  xlsx: MimeType["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  xml: MimeType["application/xml"],
  xul: MimeType["application/vnd.mozilla.xul+xml"],
  zip: MimeType["application/zip"],
  "3gp": MimeType["video/3gpp"],
  "3g2": MimeType["video/3gpp2"],
  "7z": MimeType["application/x-7z-compressed"],
  heic: MimeType["image/heic"],
  heif: MimeType["image/heif"],
  avif: MimeType["image/avif"],
  jfif: MimeType["image/jpeg"],
  mov: MimeType["video/quicktime"],
  dng: MimeType["image/x-adobe-dng"],
  esx: MimeType["application/vnd.xactware.esx"],
};

export const DEFAULT_MIME_TYPE: MimeType = "application/octet-stream";

export function getMimeTypeFromFilename(fileName: string) {
  const extension = fileName.toLowerCase().split(".").pop();
  if (!extension) {
    return DEFAULT_MIME_TYPE;
  }
  return mimeTypeMap[extension] ?? DEFAULT_MIME_TYPE;
}

const EXTENSION_BY_MIME_TYPE = Object.entries(mimeTypeMap).reduce<Partial<Record<MimeType, string>>>(
  (acc, [extension, mimeType]) => {
    acc[mimeType] ??= extension;
    return acc;
  },
  {},
);

/**
 * An extension for which `getMimeTypeFromFilename` returns `mimeType` again. `file-type`'s own
 * `ext` is not always one — it reports `ogg` for OGG-Vorbis and `spx` for Speex, where only `oga`
 * maps back to `audio/ogg` — so a name built from the raw value still reads as octet-stream.
 */
export function getExtensionForMimeType(mimeType: MimeType, sniffedExtension: string): string {
  if (mimeTypeMap[sniffedExtension.toLowerCase()] === mimeType) {
    return sniffedExtension;
  }
  return EXTENSION_BY_MIME_TYPE[mimeType] ?? sniffedExtension;
}

/** Maps normalized MIME type (lowercase, no parameters) to a typical file extension. */
const CONTENT_TYPE_TO_PREFERRED_EXTENSION: Record<string, string> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
};

/**
 * Load-bearing for the S3 upload allowlist in `@remi/s3/upload-content-types`, which gates stored
 * XSS. Changing the split or the `undefined` sentinel changes what that allowlist accepts.
 */
export function normalizeContentType(contentType: string | undefined): string | undefined {
  return contentType?.split(";")[0]?.trim().toLowerCase() || undefined;
}

// Types a browser executes rather than renders. Deliberately not the inverse of any upload allowlist:
// this is the set that must never survive as a stored header, whether it was declared or sniffed.
const BROWSER_EXECUTABLE_CONTENT_TYPES = new Set([
  "text/html",
  "text/javascript",
  "application/javascript",
  "application/x-javascript",
  "application/xhtml+xml",
  "image/svg+xml",
  "application/x-httpd-php",
  "application/x-sh",
  "application/x-csh",
  "application/x-shockwave-flash",
  "application/vnd.mozilla.xul+xml",
  "text/xml",
  "application/xml",
]);

export function isBrowserExecutableContentType(contentType: string | undefined): contentType is string {
  const normalized = normalizeContentType(contentType);
  return normalized !== undefined && BROWSER_EXECUTABLE_CONTENT_TYPES.has(normalized);
}

/** Preferred file extension for a Content-Type header value (e.g. from HTTP or API input). */
export function getFileExtensionFromContentType(contentType: string | undefined): string | undefined {
  const normalized = normalizeContentType(contentType);
  return normalized ? CONTENT_TYPE_TO_PREFERRED_EXTENSION[normalized] : undefined;
}

export const MimeTypeToFileType: Record<MimeType, FileType> = {
  "audio/aac": FileType.AUDIO,
  "application/x-abiword": FileType.DOCUMENT,
  "application/x-freearc": FileType.DOCUMENT,
  "video/x-msvideo": FileType.VIDEO,
  "application/vnd.amazon.ebook": FileType.DOCUMENT,
  "application/octet-stream": FileType.DOCUMENT,
  "image/bmp": FileType.PHOTO,
  "application/x-bzip": FileType.DOCUMENT,
  "application/x-bzip2": FileType.DOCUMENT,
  "application/x-cdf": FileType.DOCUMENT,
  "application/x-cfb": FileType.DOCUMENT,
  "application/x-csh": FileType.DOCUMENT,
  "audio/amr": FileType.AUDIO,
  "text/css": FileType.DOCUMENT,
  "text/csv": FileType.DOCUMENT,
  "application/msword": FileType.DOCUMENT,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileType.DOCUMENT,
  "application/vnd.ms-fontobject": FileType.DOCUMENT,
  "application/epub+zip": FileType.DOCUMENT,
  "application/gzip": FileType.DOCUMENT,
  "image/gif": FileType.PHOTO,
  "text/html": FileType.DOCUMENT,
  "image/vnd.microsoft.icon": FileType.PHOTO,
  "text/calendar": FileType.DOCUMENT,
  "application/java-archive": FileType.DOCUMENT,
  "image/jpeg": FileType.PHOTO,
  "text/javascript": FileType.DOCUMENT,
  "application/json": FileType.DOCUMENT,
  "application/ld+json": FileType.DOCUMENT,
  "audio/midi": FileType.AUDIO,
  "audio/x-midi": FileType.AUDIO,
  "audio/mpeg": FileType.AUDIO,
  "audio/mp4": FileType.AUDIO,
  "audio/x-m4a": FileType.AUDIO,
  "video/mp4": FileType.VIDEO,
  "application/mp4": FileType.DOCUMENT,
  "video/mpeg": FileType.VIDEO,
  "application/vnd.apple.installer+xml": FileType.DOCUMENT,
  "application/vnd.oasis.opendocument.presentation": FileType.DOCUMENT,
  "application/vnd.oasis.opendocument.spreadsheet": FileType.DOCUMENT,
  "application/vnd.oasis.opendocument.text": FileType.DOCUMENT,
  "audio/ogg": FileType.AUDIO,
  "video/ogg": FileType.VIDEO,
  "application/ogg": FileType.DOCUMENT,
  "audio/opus": FileType.AUDIO,
  "font/otf": FileType.DOCUMENT,
  "image/png": FileType.PHOTO,
  "application/pdf": FileType.DOCUMENT,
  "application/x-httpd-php": FileType.DOCUMENT,
  "application/vnd.ms-powerpoint": FileType.DOCUMENT,
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": FileType.DOCUMENT,
  "application/vnd.rar": FileType.DOCUMENT,
  "application/rtf": FileType.DOCUMENT,
  "application/x-sh": FileType.DOCUMENT,
  "image/svg+xml": FileType.PHOTO,
  "application/x-shockwave-flash": FileType.DOCUMENT,
  "application/x-tar": FileType.DOCUMENT,
  "image/tiff": FileType.PHOTO,
  "video/mp2t": FileType.VIDEO,
  "font/ttf": FileType.DOCUMENT,
  "text/plain": FileType.DOCUMENT,
  "text/vcard": FileType.DOCUMENT,
  "text/x-vcard": FileType.DOCUMENT,
  "application/vnd.visio": FileType.DOCUMENT,
  "audio/wav": FileType.AUDIO,
  "audio/webm": FileType.AUDIO,
  "video/webm": FileType.VIDEO,
  "image/webp": FileType.PHOTO,
  "font/woff": FileType.DOCUMENT,
  "font/woff2": FileType.DOCUMENT,
  "application/xhtml+xml": FileType.DOCUMENT,
  "application/vnd.ms-excel": FileType.DOCUMENT,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": FileType.DOCUMENT,
  "application/xml": FileType.DOCUMENT,
  "application/vnd.mozilla.xul+xml": FileType.DOCUMENT,
  "application/zip": FileType.DOCUMENT,
  "video/3gpp": FileType.VIDEO,
  "video/3gpp2": FileType.VIDEO,
  "application/x-7z-compressed": FileType.DOCUMENT,
  "video/quicktime": FileType.VIDEO,
  "image/jp2": FileType.PHOTO,
  "image/heic": FileType.PHOTO,
  "image/heif": FileType.PHOTO,
  "image/avif": FileType.PHOTO,
  "image/x-adobe-dng": FileType.PHOTO,
  "application/vnd.xactware.esx": FileType.DOCUMENT,
  "message/rfc822": FileType.DOCUMENT,
  "multipart/related": FileType.DOCUMENT,
};
